import {
  Button,
  GreyGrey20,
  GreyGrey30,
  GreyGrey50,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  Wordmark,
  SequenceMenu,
  SequenceMenuItemType,
  IconCircleQuestionmark,
  IconArrowUpRight,
  IconBookOpen,
  IconGraduateCap,
  IconNewspaper,
  IconFlag,
  IconConsole,
  IconShieldCheck,
  useCookieConsent,
  Box,
  Flex
} from '@sequencehq/core-components'
import { ShareFeedbackModal } from 'components/ShareFeedbackModal/ShareFeedbackModal'
import { useFooter } from 'components/NavigationV3/components/Footer/useFooter'
import { useKeyboardShortcuts } from '../../../../lib/keyboardShortcuts/useKeyboardShortcuts'
import { useNavigationV3Context } from 'components/NavigationV3/hooks/useNavigationV3Context'
import { KeyboardShortcutsOverlay } from 'components/KeyboardShortcutsOverlay/KeyboardShortcutsOverlay'

export const Footer = () => {
  const { feedbackModal } = useFooter()
  const { reopen: reopenConsentBanner } = useCookieConsent()
  const { isKeyboardShortcutsOpen, setIsKeyboardShortcutsOpen } =
    useKeyboardShortcuts()
  const { data } = useNavigationV3Context()
  // `isEffectivelyCollapsed`, not the persisted preference: a phone drawer
  // always shows the expanded nav, so its footer must match.
  const { isEffectivelyCollapsed: isNavigationCollapsed } = data

  const items: SequenceMenuItemType[] = [
    {
      label: 'Keyboard shortcuts',
      iconLeft: <IconConsole color="inherit" />,
      onClick: () => setIsKeyboardShortcutsOpen(true),
      divider: true
    },
    {
      label: 'Documentation',
      iconLeft: <IconBookOpen color="inherit" />,
      iconRight: <IconArrowUpRight color="inherit" />,
      onClick: () =>
        window.open(
          'https://docs.sequencehq.com/reference/gettingStarted',
          '_blank'
        )
    },
    {
      label: 'Academy',
      iconLeft: <IconGraduateCap color="inherit" />,
      iconRight: <IconArrowUpRight color="inherit" />,
      onClick: () =>
        window.open(
          'https://docs.sequencehq.com/getting-started/overview',
          '_blank'
        )
    },
    {
      label: 'Changelog',
      iconLeft: <IconNewspaper color="inherit" />,
      iconRight: <IconArrowUpRight color="inherit" />,
      onClick: () =>
        window.open('https://www.sequencehq.com/changelog', '_blank')
    },
    {
      label: 'Share feedback',
      iconLeft: <IconFlag color="inherit" />,
      onClick: feedbackModal.toggle,
      'data-testid': 'help-menu.share-feedback',
      divider: true
    },
    {
      label: 'Cookie settings',
      iconLeft: <IconShieldCheck color="inherit" />,
      onClick: reopenConsentBanner,
      'data-testid': 'help-menu.cookie-settings'
    }
  ]

  return (
    <>
      <Flex
        width="100%"
        height="40px"
        px={isNavigationCollapsed ? '4px' : '8px'}
        justifyContent={isNavigationCollapsed ? 'center' : 'space-between'}
        alignItems="center"
        position="relative"
      >
        {!isNavigationCollapsed && (
          <Box p="8px">
            <Wordmark
              width="99px"
              height="22px"
              color={GreyGrey50}
              data-testid="sequence-logo"
            />
          </Box>
        )}

        <SequenceMenu
          menuButton={
            <Button
              variant="unstyled"
              color={GreyGrey60}
              style={{
                display: 'flex',
                width: '28px',
                height: '28px',
                minWidth: 'initial'
              }}
              hover={{ color: GreyGrey70, background: GreyGrey20 }}
              activeStyle={{ color: GreyGrey80, background: GreyGrey30 }}
              focus={{ color: GreyGrey80, background: GreyGrey30 }}
              data-testid="help-menu-button"
            >
              <IconCircleQuestionmark color="inherit" />
            </Button>
          }
          items={items}
          // Centred on the button: the menu is wider than the space to the
          // right of it on a phone, so aligning either edge runs off-screen.
          alignment="top-center"
        />

        {feedbackModal.visible && (
          <ShareFeedbackModal onClose={feedbackModal.toggle} />
        )}
      </Flex>

      <KeyboardShortcutsOverlay
        isOpen={isKeyboardShortcutsOpen}
        onClose={() => setIsKeyboardShortcutsOpen(false)}
      />
    </>
  )
}
